/**
 * 改造自threejs的FirstPersonControls
 * 操作方式：
 *  W或↑ 向前移动相机
 *  S或↓ 向后移动相机
 *  A或← 向左移动相机
 *  D或→ 向右移动相机
 *  Q    向上移动相机
 *  E    向下移动相机
 *  空格+W/A/S/D/Q/E  非常缓慢移动相机
 *  shift+W/A/S/D/Q/E 快速移动相机
 *  shift+空格+W/A/S/D/Q/E 非常快移动相机
 *
 *  鼠标右键+移动鼠标   旋转相机（围绕相机自身）
 * 
 *  Alt+鼠标右键+移动鼠标   旋转相机（绕着目标点旋转）
 *
 *  鼠标中间+移动鼠标   平移相机
 *  空格键+鼠标中间+移动鼠标  非常缓慢地平移相机
 *  SHIFT+鼠标中间+移动鼠标  更快地平移相机
 *  SHIFT+空格+鼠标中间+移动鼠标   平移相机非常快
 *
 *  鼠标滚轮 上/下   向前/向后移动相机(目标点为光标位置)
 *  shift+鼠标滚轮 上/下   快速向前/向后移动相机(目标点为光标位置)
 *  shift+空格键+鼠标滚轮 上/下   非常快向前/向后移动相机(目标点为光标位置)
 *  空格键+鼠标滚轮 上/下   非常缓慢地向前/向后移动相机(目标点为光标位置)
 *
 * 注意：迅雷插件会默认开启shift+d多选下载，需要手动关闭。
 * 
 * 使用方式：
 *    初始化：
 *        this.customControls = new CustomControls(
            this.activeCamera,
            this.renderer.domElement
          );
      更新： 需要每帧调用  if (this.customControls) this.customControls.update(dt);
      窗口重置大小： if (this.customControls) this.customControls.resize();
      销毁：if(this.customControls) this.customControls.dispose()
 */
import { Vector2, MathUtils, Spherical, Vector3, Quaternion } from "three";

const _lookDirection = new Vector3();
const _spherical = new Spherical();
const _target = new Vector3();

class CustomControls {
  constructor(object, domElement) {
    if (domElement === undefined) {
      console.warn(
        'CustomControls: The second parameter "domElement" is now mandatory.'
      );
      domElement = document;
    }

    this.object = object;
    this.domElement = domElement;

    // API

    this.enabled = true; // 是否启用控制器

    this.movementSpeed = 5.0; // 移动速度
    this.lookSpeed = 5; // 旋转速度
    this.panSpeed = 1; // 平移速度
    this.zoomSpeed = 0.5; // 缩放速度

    this.lookVertical = true; // 是否允许垂直方向上旋转视角
    this.enablePan = true; // 是否允许鼠标中键平移视角
    this.enableZoom = true; // 是否允许鼠标滚轮缩放视角

    this.heightSpeed = false;
    this.heightCoef = 1.0;
    this.heightMin = 0.0;
    this.heightMax = 1.0;

    this.constrainVertical = false;
    this.verticalMin = 0;
    this.verticalMax = Math.PI;

    this.mouseDragOn = false;

    // internals
    this.rotateEndX = 0;
    this.rotateEndY = 0;
    this.rotateStartX = 0;
    this.rotateStartY = 0;

    // rotate target
    this.isRotateTarget = false;
    this.rotateTergetRadius = 5; // 围绕目标点旋转的半径

    this.moveForward = false;
    this.moveBackward = false;
    this.moveLeft = false;
    this.moveRight = false;
    this.spaceKey = false;
    this.shiftKey = false;
    this.altKey = false;

    this.viewHalfX = 0;
    this.viewHalfY = 0;

    // pan
    const panStart = new Vector2();
    const panEnd = new Vector2();
    const panDelta = new Vector2();

    this.lastWheelPos = new Vector2();

    // private variables

    this.lat = 0;
    this.lon = 0;
    let _delta = 0;

    // 关闭浏览器默认的右键菜单
    document.oncontextmenu = function () {
      return false;
    };

    //

    this.resize = function () {
      if (this.domElement === document) {
        this.viewHalfX = window.innerWidth / 2;
        this.viewHalfY = window.innerHeight / 2;
      } else {
        this.viewHalfX = this.domElement.offsetWidth / 2;
        this.viewHalfY = this.domElement.offsetHeight / 2;
      }
    };

    this.onMouseDown = function (event) {
      // 鼠标右键
      if (event.button === 2) {
        if (this.domElement === document) {
          this.rotateStartX = event.pageX - this.viewHalfX;
          this.rotateStartY = event.pageY - this.viewHalfY;
        } else {
          this.rotateStartX =
            event.pageX - this.domElement.offsetLeft - this.viewHalfX;
          this.rotateStartY =
            event.pageY - this.domElement.offsetTop - this.viewHalfY;
        }

        // 按住alt键和鼠标右键时绕着正前方目标点旋转
        if (this.altKey) {
          this.isRotateTarget = true;
          // 计算正前方2米处的目标点
          this.targetPoint = new Vector3();
          this.object.getWorldDirection(this.targetPoint); // 获取相机当前朝向的单位向量
          this.targetPoint
            .multiplyScalar(this.rotateTergetRadius)
            .add(this.object.position); // 沿方向向量延伸2米

          const direction = new Vector3().subVectors(
            this.object.position,
            this.targetPoint
          );
          const radius = direction.length();
          this.phi = Math.acos(direction.y / radius); // 俯仰角
          this.theta = Math.atan2(direction.z, direction.x); // 水平角
        } else {
          // 先获取当前相机的经纬度
          setOrientation(this);

          this.isRotateTarget = false;
        }

        this.mouseDragOn = true;
      }
      // 鼠标中键
      else if (event.button === 1) {
        this.centerMouseKey = true;

        panStart.set(event.clientX, event.clientY);
      }
    };

    this.onMouseUp = function (event) {
      event.preventDefault();

      // 鼠标右键
      if (event.button === 2) {
        this.mouseDragOn = false;
      }
      // 鼠标中键
      else if (event.button === 1) {
        this.centerMouseKey = false;
      }
    };

    this.onMouseMove = function (event) {
      // 按下鼠标右键并拖动鼠标：旋转视角
      if (this.mouseDragOn) {
        if (this.domElement === document) {
          this.rotateEndX = event.pageX - this.viewHalfX;
          this.rotateEndY = event.pageY - this.viewHalfY;
        } else {
          this.rotateEndX =
            event.pageX - this.domElement.offsetLeft - this.viewHalfX;
          this.rotateEndY =
            event.pageY - this.domElement.offsetTop - this.viewHalfY;
        }

        if (!this.isRotateTarget) this.updateRotate();
        else {
          this.updateRotateTarget();
        }
      }
      // 按下鼠标中键并拖动鼠标：平移视角
      else if (this.centerMouseKey) {
        panEnd.set(event.clientX, event.clientY);

        panDelta.subVectors(panEnd, panStart).multiplyScalar(this.panSpeed);

        this.updatePan(panDelta.x, panDelta.y);

        panStart.copy(panEnd);
      }
    };

    this.onKeyDown = function (event) {
      switch (event.code) {
        case "ArrowUp":
        case "KeyW":
          this.moveForward = true;
          break;

        case "ArrowLeft":
        case "KeyA":
          this.moveLeft = true;
          break;

        case "ArrowDown":
        case "KeyS":
          this.moveBackward = true;
          break;

        case "ArrowRight":
        case "KeyD":
          this.moveRight = true;
          break;

        case "KeyQ":
          this.moveUp = true;
          break;
        case "KeyE":
          this.moveDown = true;
          break;

        case "Space":
          this.spaceKey = true;
          break;
        case "ShiftLeft":
        case "ShiftRight":
          this.shiftKey = true;
          break;
        case "AltLeft":
        case "AltRight":
          this.altKey = true;
          break;
      }
    };

    this.onKeyUp = function (event) {
      switch (event.code) {
        case "ArrowUp":
        case "KeyW":
          this.moveForward = false;
          break;

        case "ArrowLeft":
        case "KeyA":
          this.moveLeft = false;
          break;

        case "ArrowDown":
        case "KeyS":
          this.moveBackward = false;
          break;

        case "ArrowRight":
        case "KeyD":
          this.moveRight = false;
          break;

        case "KeyQ":
          this.moveUp = false;
          break;
        case "KeyE":
          this.moveDown = false;
          break;

        case "Space":
          this.spaceKey = false;
          break;
        case "ShiftLeft":
        case "ShiftRight":
          this.shiftKey = false;
          break;
        case "AltLeft":
        case "AltRight":
          this.altKey = false;
          break;
      }
    };

    this.onMouseWheel = function (event) {
      event.preventDefault();

      if (this.object.isPerspectiveCamera) {
        if (
          !this.lastWheelPos ||
          !this.lastWheelPos.equals(new Vector2(event.offsetX, event.offsetY))
        ) {
          this.lastWheelPos.set(event.offsetX, event.offsetY);
          this.moveDir = this.getCurrentMouseDir(event.offsetX, event.offsetY);
        }

        let actualMoveSpeed = this.zoomSpeed * _delta;
        if (this.spaceKey && !this.shiftKey) {
          actualMoveSpeed /= 5;
        } else if (!this.spaceKey && this.shiftKey) {
          actualMoveSpeed *= 2;
        } else if (this.spaceKey && this.shiftKey) {
          actualMoveSpeed *= 5;
        }

        // 拉近
        if (event.deltaY < 0) {
          this.object.position.lerp(this.moveDir.forward, actualMoveSpeed);
        }
        // 远离
        else if (event.deltaY > 0) {
          this.object.position.lerp(this.moveDir.back, actualMoveSpeed);
        }
      } else {
        // 拉近
        if (event.deltaY < 0) {
          this.object.zoom = Math.max(
            0,
            this.object.zoom / this.getZoomScale()
          );
          this.object.updateProjectionMatrix();
        }
        // 远离
        else if (event.deltaY > 0) {
          this.object.zoom = Math.max(
            0,
            this.object.zoom * this.getZoomScale()
          );
          this.object.updateProjectionMatrix();
        }
      }
    };

    this.getZoomScale = function () {
      return Math.pow(0.95, this.zoomSpeed);
    };

    this.getCurrentMouseDir = function (x, y) {
      // 屏幕坐标转标准设备坐标
      const x1 = (x / this.domElement.width) * 2 - 1;
      const y1 = -(y / this.domElement.height) * 2 + 1;
      //标准设备坐标(z=0.5这个值并没有一个具体的说法)
      const stdVector = new Vector3(x1, y1, 0.5);
      const worldVector = stdVector.clone().unproject(this.object);
      //获取摄像机世界坐标
      let cameraPos = this.object.getWorldPosition(new Vector3());
      //获取射线方向
      let forwordDir = worldVector.clone().sub(cameraPos).normalize();
      let forwardTarget = cameraPos
        .clone()
        .add(forwordDir.clone().multiplyScalar(100));

      let backDir = forwordDir.negate();
      let backTarget = cameraPos
        .clone()
        .add(backDir.clone().multiplyScalar(100));

      return {
        forward: forwardTarget,
        back: backTarget,
      };
    };

    this.lookAt = function (x, y, z) {
      if (x.isVector3) {
        _target.copy(x);
      } else {
        _target.set(x, y, z);
      }

      this.object.lookAt(_target);

      setOrientation(this);

      return this;
    };

    // 相机移动
    this.update = function (delta) {
      if (this.enabled === false) return;

      if (!this.object.isPerspectiveCamera) return; // 忽略非透视相机

      _delta = delta;

      let actualMoveSpeed = delta * this.movementSpeed;
      if (this.spaceKey && !this.shiftKey) {
        actualMoveSpeed /= 5;
      } else if (!this.spaceKey && this.shiftKey) {
        actualMoveSpeed *= 2;
      } else if (this.spaceKey && this.shiftKey) {
        actualMoveSpeed *= 5;
      }

      if (this.moveForward) this.object.translateZ(-actualMoveSpeed);
      if (this.moveBackward) this.object.translateZ(actualMoveSpeed);

      if (this.moveLeft) this.object.translateX(-actualMoveSpeed);
      if (this.moveRight) this.object.translateX(actualMoveSpeed);

      if (this.moveUp) this.object.translateY(actualMoveSpeed);
      if (this.moveDown) this.object.translateY(-actualMoveSpeed);
    };

    // 相机旋转(围绕自身)
    this.updateRotate = function () {
      if (!this.object.isPerspectiveCamera) return; // 忽略非透视相机

      let actualLookSpeed = _delta * this.lookSpeed;
      let verticalLookRatio = 1;
      if (this.constrainVertical) {
        verticalLookRatio = Math.PI / (this.verticalMax - this.verticalMin);
      }
      this.lon -= (this.rotateEndX - this.rotateStartX) * actualLookSpeed;
      if (this.lookVertical) {
        this.lat -=
          (this.rotateEndY - this.rotateStartY) *
          actualLookSpeed *
          verticalLookRatio;
      }
      this.lat = Math.max(-85, Math.min(85, this.lat));
      let phi = MathUtils.degToRad(90 - this.lat);
      const theta = MathUtils.degToRad(this.lon);
      if (this.constrainVertical) {
        phi = MathUtils.mapLinear(
          phi,
          0,
          Math.PI,
          this.verticalMin,
          this.verticalMax
        );
      }
      const position = this.object.position;
      const targetPosition = new Vector3();
      targetPosition.setFromSphericalCoords(1, phi, theta).add(position);
      this.object.lookAt(targetPosition);
      this.rotateStartX = this.rotateEndX;
      this.rotateStartY = this.rotateEndY;
    };

    // 围绕相机正前方旋转（绕目标点）
    this.updateRotateTarget = function () {
      if (!this.object.isPerspectiveCamera) return; // 忽略非透视相机

      // 计算鼠标移动增量
      const deltaX = this.rotateEndX - this.rotateStartX;
      const deltaY = this.rotateEndY - this.rotateStartY;
      this.rotateStartX = this.rotateEndX;
      this.rotateStartY = this.rotateEndY;

      let actualLookSpeed = (_delta * this.lookSpeed) / 40;
      // 根据移动量调整角度
      this.theta += deltaX * actualLookSpeed; // 水平旋转速度系数
      this.phi -= deltaY * actualLookSpeed; // 俯仰旋转速度系数

      // 限制俯仰角范围（避免翻转）
      this.phi = Math.max(0.1, Math.min(Math.PI - 0.1, this.phi));

      // 更新相机位置
      this.object.position.x =
        this.targetPoint.x +
        this.rotateTergetRadius * Math.sin(this.phi) * Math.cos(this.theta);
      this.object.position.y =
        this.targetPoint.y + this.rotateTergetRadius * Math.cos(this.phi);
      this.object.position.z =
        this.targetPoint.z +
        this.rotateTergetRadius * Math.sin(this.phi) * Math.sin(this.theta);
      this.object.lookAt(this.targetPoint);
    };

    // 相机平移
    this.updatePan = function (deltaX, deltaY) {
      if (!this.enablePan) return;

      let actualMoveSpeed = _delta * this.panSpeed;
      if (this.spaceKey && !this.shiftKey) {
        actualMoveSpeed /= 5;
      } else if (!this.spaceKey && this.shiftKey) {
        actualMoveSpeed *= 2;
      } else if (this.spaceKey && this.shiftKey) {
        actualMoveSpeed *= 5;
      }

      this.object.translateX(-actualMoveSpeed * deltaX);
      this.object.translateY(actualMoveSpeed * deltaY);
    };

    this.dispose = function () {
      //this.domElement.removeEventListener("contextmenu", contextmenu);
      this.domElement.removeEventListener("pointerdown", _onMouseDown);
      this.domElement.removeEventListener("pointermove", _onMouseMove);
      this.domElement.removeEventListener("pointerup", _onMouseUp);
      this.domElement.removeEventListener("wheel", _onMouseWheel);

      window.removeEventListener("keydown", _onKeyDown);
      window.removeEventListener("keyup", _onKeyUp);
    };

    const _onMouseMove = this.onMouseMove.bind(this);
    const _onMouseDown = this.onMouseDown.bind(this);
    const _onMouseUp = this.onMouseUp.bind(this);
    const _onKeyDown = this.onKeyDown.bind(this);
    const _onKeyUp = this.onKeyUp.bind(this);
    const _onMouseWheel = this.onMouseWheel.bind(this);

    //this.domElement.addEventListener("contextmenu", contextmenu);
    this.domElement.addEventListener("pointermove", _onMouseMove);
    this.domElement.addEventListener("pointerdown", _onMouseDown);
    this.domElement.addEventListener("pointerup", _onMouseUp);
    this.domElement.addEventListener("wheel", _onMouseWheel, {
      passive: false,
    });

    window.addEventListener("keydown", _onKeyDown);
    window.addEventListener("keyup", _onKeyUp);

    function setOrientation(controls) {
      const quaternion = controls.object.quaternion;

      _lookDirection.set(0, 0, -1).applyQuaternion(quaternion);
      _spherical.setFromVector3(_lookDirection);

      controls.lat = 90 - MathUtils.radToDeg(_spherical.phi);
      controls.lon = MathUtils.radToDeg(_spherical.theta);
    }

    setOrientation(this);
  }
}

export { CustomControls };
